import { withStyles } from '@material-ui/core';
import { useCallback, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Rectangle, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import Snackbar from '../Snackbar/Snackbar';

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
        <div style={{ backgroundColor: '#fff', border: '1px solid #ccc', padding: '8px 12px' }}>
            <p style={{ margin: 0 }}>{label}</p>
            <p style={{ color: '#000', margin: '4px 0 0' }}>
                suma: {payload[0].value}
            </p>
        </div>
    );
};

const styles = {
    container: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
        height: 350,
        width: '100%',
    },
    title: {
        color: '#595652',
        marginBottom: '0.5rem',
        fontSize: '0.9rem',
        textAlign: 'center',
    },
};

const tickFormatter = tick => {
    const date = new Date(tick);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');

    return `${day}-${month}`;
};

const yAxisFormatter = value => {
    if (value >= 1000000) {
        return `${(value / 1000000).toFixed(value % 1000000 === 0 ? 0 : 1)} mln`;
    }

    if (value >= 1000) {
        return `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}k`;
    }

    return String(value);
};

const COLORS = {
    green: '#81C784',
    red: '#E57373',
    blue: '#64B5F6',
};

const DeviationBarShape = props => {
    const { payload, combinedData, deviationSettings, daysBack } = props;
    let barColor = COLORS.green;

    if (deviationSettings && combinedData && payload && daysBack > 0) {
        const barIndex = combinedData.findIndex(d => d.day === payload.day);

        if (barIndex >= daysBack) {
            const prev = combinedData.slice(barIndex - daysBack, barIndex);
            const avg = prev.reduce((s, d) => s + d.count, 0) / daysBack;
            const upper = avg * (1 + Math.abs(deviationSettings.deviation_positive) / 100);
            const lower = avg * (1 - Math.abs(deviationSettings.deviation_negative) / 100);

            if (payload.count > upper) {
                barColor = COLORS.red;
            } else if (payload.count < lower) {
                barColor = COLORS.blue;
            }
        }
    }

    return <Rectangle {...props} fill={barColor} style={{ cursor: 'pointer' }} />;
};

const StatisticsChart = ({ classes: { container, title }, data, combinedData, title: chartTitle, dataKey, color, deviationSettings, daysBack }) => {
    const [snackbarData, setSnackbarData] = useState({
        isSnackbarOpen: false,
        message: '',
        errorInfo: '',
        variant: '',
    });

    const handleSnackbarClose = useCallback(() => {
        setSnackbarData(prev => ({ ...prev, isSnackbarOpen: false }));
    }, []);

    const handleBarClick = useCallback(data => {
        if (data?.count !== undefined) {
            const val = String(data.count);
            navigator.clipboard.writeText(val);
            setSnackbarData({
                isSnackbarOpen: true,
                message: `Skopiowano: ${val}`,
                errorInfo: '',
                variant: 'success',
            });
        }
    }, []);

    return (
        <div className={container}>
            {chartTitle && <h3 className={title}>{chartTitle}</h3>}

            <ResponsiveContainer width="95%" height="85%">
                <BarChart width={600} height={300} data={data} margin={{ bottom: 50, left: 0 }}>
                    <CartesianGrid strokeDasharray="5 5" />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(128, 128, 128, 0.15)' }} />
                    <XAxis
                        dataKey="day"
                        dy={12}
                        angle={-90}
                        tickMargin={10}
                        height={60}
                        tickFormatter={tickFormatter}
                        tick={{ fontWeight: '600', fontSize: 13 }}
                    />
                    <YAxis tickFormatter={yAxisFormatter} tick={{ fontWeight: '600', fontSize: 13 }} allowDecimals={false} width={50} axisLine={false} tickLine={false} />
                    <Bar dataKey={dataKey} fill={color} onClick={handleBarClick} shape={<DeviationBarShape combinedData={combinedData} deviationSettings={deviationSettings} daysBack={daysBack} />} />
                </BarChart>
            </ResponsiveContainer>

            <Snackbar snackbarData={snackbarData} handleIsSnackbarOpen={handleSnackbarClose} />
        </div>
    );
};

export default withStyles(styles)(StatisticsChart);
